import classNames from "classnames"

import { Color } from "../../../../constants/V2/color"
import BulletItem from "../../../elements/V2/BulletItem"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import IconInfoBlock from "../../../elements/V2/IconInfoBlock"
import ArrowIcon from "../../../elements/V2/Icons/ArrowIcon"
import ListItem from "../../../elements/V2/ListItem"
import Typography from "../../../elements/V2/Typography"
import LayoutItem from "../../Layouts/LayoutItem"

import PillButton from "@components/elements/V2/Buttons/PillButton"
import VideoButton from "@components/elements/V2/Buttons/VideoButton"
import PriceBadge, { PriceBadgeProps } from "@components/elements/V2/priceBadge"
import { ButtonStyle } from "@constants/V2/buttons"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"
import React from "react"

type MasterBulletPointsProps = {
  itemType: "bullet_items" | "icon_info" | "list_items"
  backgroundColor?: Color
  superscriptText?: string
  descriptionText?: string
  headerSize?: "small" | "large"
  textAlignment?: "left" | "center"
  title?: string
  body?: string
  bulletItems?: React.ComponentProps<typeof BulletItem>[]
  listItems?: React.ComponentProps<typeof ListItem>[]
  iconInfoBlocks?: Omit<
    React.ComponentProps<typeof IconInfoBlock>,
    "size" | "textColor"
  >[]
  priceBadge?: PriceBadgeProps
  isNumbered?: boolean
  linkText?: string
  linkUrl?: string | YocoCom.YocoLink
  linkColor?: Color
  mobileGridOrder?: number
  linkStyle?: ButtonStyle
  videoCoverImage?: string
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
}

const MasterBulletPoints = ({
  itemType,
  backgroundColor,
  superscriptText,
  descriptionText,
  title,
  body,
  textAlignment = "left",
  headerSize = "small",
  bulletItems,
  iconInfoBlocks,
  priceBadge,
  isNumbered,
  listItems,
  linkText,
  linkUrl,
  linkColor,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  mobileGridOrder,
  linkStyle = ButtonStyle.Text,
  videoCoverImage,
  ...props
}: MasterBulletPointsProps) => {
  const typographyColor = backgroundColor
    ? getAccentColor(backgroundColor)
    : Color.Charcoal

  const getCTA = () => {
    if (!linkText || !linkUrl) return null

    if (linkStyle === ButtonStyle.Text) {
      return (
        <TextButton
          style="icon"
          text={linkText}
          icon={<ArrowIcon />}
          linkUrl={linkUrl}
          color={linkColor || typographyColor}
          trackingEvent={trackingEvent}
          trackingEventKey={trackingEventKey}
          trackingEventValue={trackingEventValue}
          className={classNames({
            "mx-auto sm:mx-0": itemType === "list_items",
          })}
        />
      )
    } else if (linkStyle === ButtonStyle.Video && videoCoverImage) {
      return (
        <VideoButton
          linkText={linkText}
          linkUrl={linkUrl}
          videoCoverImage={videoCoverImage}
          color={linkColor || typographyColor}
          //TODO add linkQueryParam support to all buttons
        />
      )
    } else {
      return (
        <PillButton
          overrideTextColor={typographyColor}
          text={linkText}
          linkUrl={linkUrl}
          color={linkColor || typographyColor}
          trackingEvent={trackingEvent}
          trackingEventKey={trackingEventKey}
          trackingEventValue={trackingEventValue}
          className={classNames({
            "mx-auto sm:mx-0": itemType === "list_items",
          })}
          style="bordered"
          size="small"
        />
      )
    }
  }

  return (
    <LayoutItem
      animateOnScroll
      mobileGridOrder={mobileGridOrder}
      as="div"
      className={classNames(
        backgroundColor ? getBackgroundColorClass(backgroundColor) : "",
        "rounded-lg col-span-4 flex flex-col gap-24 px-24 py-32 sm:col-span-full md:col-span-3 md:gap-32 md:px-32 md:py-48 lg:col-span-6 lg:gap-40 lg:px-48 lg:py-64"
      )}
      {...props}
    >
      <div className="flex flex-col gap-8 md:gap-16">
        {superscriptText && (
          <Typography
            font="grotesk"
            weight="book"
            size="subscript-lg"
            color={typographyColor}
            text={superscriptText}
            className={classNames(
              textAlignment === "left" ? "text-left" : "text-center",
              "uppercase"
            )}
          />
        )}
        {title &&
          (headerSize === "small" ? (
            <Typography
              font="grotesk"
              weight="book"
              size="h5"
              text={title}
              color={typographyColor}
              element="h3"
              className={textAlignment === "left" ? "text-left" : "text-center"}
            />
          ) : (
            <Typography
              font="grotesk"
              weight="medium"
              size="h3"
              color={typographyColor}
              text={title}
              className={textAlignment === "left" ? "text-left" : "text-center"}
              element="h3"
            />
          ))}
      </div>
      {(descriptionText || body) && (
        <>
          {descriptionText && (
            <Typography
              font="grotesk"
              weight="book"
              size="body-lg"
              color={typographyColor}
              text={descriptionText}
              className={textAlignment === "left" ? "text-left" : "text-center"}
            />
          )}
        </>
      )}

      {itemType === "icon_info" && iconInfoBlocks && (
        <div className="grid grid-cols-1 gap-32 md:grid-cols-2">
          {iconInfoBlocks.map(
            ({ iconUrl, iconAlt, titleText, bodyText }, index) => (
              <IconInfoBlock
                key={`${titleText}-${index}`}
                iconUrl={iconUrl}
                iconAlt={iconAlt}
                titleText={titleText}
                bodyText={bodyText}
                size={"small"}
                textColor={typographyColor}
              />
            )
          )}
        </div>
      )}

      {itemType === "bullet_items" && bulletItems && (
        <div className="flex flex-col gap-16">
          {bulletItems.map((bulletItem, index) => (
            <BulletItem
              key={`${bulletItem.text}-${index}`}
              text={bulletItem.text}
              answerText={bulletItem.answerText}
              answerTextPosition="below"
              size="body-md"
              textColor={typographyColor}
              iconOverrideColor={typographyColor}
              isNumbered={isNumbered}
              number={index + 1}
            />
          ))}
        </div>
      )}

      {itemType === "list_items" && listItems && (
        <div className="flex flex-col gap-32">
          {listItems.map(({ titleText, bodyText }, index) => (
            <ListItem
              bodyText={bodyText}
              titleText={titleText}
              listNumber={index + 1}
              textColor={typographyColor}
              key={`${titleText}-${index}`}
            />
          ))}
        </div>
      )}

      {getCTA()}

      {priceBadge && (
        <PriceBadge
          text={priceBadge.text}
          highlight={priceBadge.highlight}
          price={priceBadge.price}
          backgroundColor={priceBadge.backgroundColor}
        />
      )}
    </LayoutItem>
  )
}

export default MasterBulletPoints
